// webapp/frontend/src/components/WaterTraceDashboard.js
import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import GroundwaterMap from './GroundwaterMap';
import LoadingScreen from './LoadingScreen';
import './WaterTrace.css';
import API_URL from '../config';

const WaterTraceDashboard = () => {
  const [historicalData, setHistoricalData] = useState([]);
  const [recentData, setRecentData] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  const [gldasAnalysis, setGldasAnalysis] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStage, setLoadingStage] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const loadingStages = [
    "Initializing ML prediction system...",
    "Loading trained regression model (this may take up to 1 minute)...",
    "Connecting to satellite feature servers...",
    "Loading GRACE training features (2002-2017)...",
    "Loading GLDAS validation features (2018-2024)...",
    "Processing district-level predictions...",
    "Running model inference and trend analysis...",
    "Preparing prediction visualizations..."
  ];

  const fetchData = async () => {
    try {
      console.log('API URL:', API_URL);
      console.log('Fetching data from WaterTrace backend...');
      
      setLoadingStage(1); // Waking up service
      
      // Add timeout for fetch requests to handle cold starts
      const fetchWithTimeout = (url, timeout = 60000) => {
        return Promise.race([
          fetch(url),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Request timeout')), timeout)
          )
        ]);
      };
      
      // Try to wake up the service with a simple request first
      try {
        await fetchWithTimeout(`${API_URL}/api/analysis/summary`, 60000);
        setLoadingStage(2); // Service is awake, connecting to servers
      } catch (error) {
        console.log('Service wake-up in progress...');
      }
      
      // Fetch all data
      const [historicalRes, recentRes, summaryRes, gldasRes, combinedRes] = await Promise.all([
        fetchWithTimeout(`${API_URL}/api/historical/timeseries`).then(res => {
          setLoadingStage(3); // GRACE data fetched
          return res;
        }),
        fetchWithTimeout(`${API_URL}/api/recent/timeseries`).then(res => {
          setLoadingStage(4); // GLDAS data fetched
          return res;
        }), 
        fetchWithTimeout(`${API_URL}/api/analysis/summary`),
        fetchWithTimeout(`${API_URL}/api/gldas/trend-analysis`),
        fetchWithTimeout(`${API_URL}/api/combined/timeline`)
      ]);

      setLoadingStage(5); // Processing district information

      const historical = await historicalRes.json();
      const recent = await recentRes.json();
      const summaryData = await summaryRes.json();
      const gldasData = await gldasRes.json();
      const combinedTimeline = await combinedRes.json();

      setLoadingStage(6); // Analyzing trends

      setHistoricalData(historical.data || []);
      setRecentData(recent.data || []);
      
      // Use properly scaled combined data from backend
      setCombinedData(combinedTimeline.data || []);
      setSummary(summaryData);
      setGldasAnalysis(gldasData);
      
      setLoadingStage(7); // Preparing visualizations
      
      // Small delay to show final stage
      setTimeout(() => {
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingScreen loadingStages={loadingStages} currentStage={loadingStage} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8 text-center px-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-blue-900 mb-2">
            WaterTrace: Pakistan Groundwater Monitoring
          </h1>
          <p className="text-blue-700 text-sm sm:text-base md:text-lg mb-2">
            Machine Learning-powered groundwater prediction system using 22 years of satellite data (2002-2024)
          </p>
          <p className="text-blue-600 text-xs sm:text-sm">
            Developed by <a href="https://github.com/tayyabmanan" target="_blank" rel="noopener noreferrer" className="font-semibold hover:text-blue-800 underline">Tayyab Manan</a>
          </p>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-8">
            <Card className="bg-white/80 backdrop-blur-sm border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-blue-600">Study Area</CardTitle>
              </CardHeader>
              <CardContent className="pt-1 sm:pt-2">
                <div className="text-xl sm:text-2xl font-bold text-blue-900">{summary.study_area.districts}</div>
                <p className="text-[10px] sm:text-xs text-blue-600">Districts</p>
              </CardContent>
            </Card>

            <Card className="bg-white/80 backdrop-blur-sm border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-blue-600">Historical</CardTitle>
              </CardHeader>
              <CardContent className="pt-1 sm:pt-2">
                <div className="text-xl sm:text-2xl font-bold text-blue-900">{summary.datasets.historical.data_points}</div>
                <p className="text-[10px] sm:text-xs text-blue-600">GRACE data</p>
              </CardContent>
            </Card>

            <Card className="bg-white/80 backdrop-blur-sm border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-blue-600">Recent</CardTitle>
              </CardHeader>
              <CardContent className="pt-1 sm:pt-2">
                <div className="text-xl sm:text-2xl font-bold text-blue-900">{summary.datasets.recent.data_points}</div>
                <p className="text-[10px] sm:text-xs text-blue-600">GLDAS data</p>
              </CardContent>
            </Card>

            <Card className="bg-white/80 backdrop-blur-sm border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-blue-600">Coverage</CardTitle>
              </CardHeader>
              <CardContent className="pt-1 sm:pt-2">
                <div className="text-xl sm:text-2xl font-bold text-blue-900">22</div>
                <p className="text-[10px] sm:text-xs text-blue-600">Years</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Analysis Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <div className="w-full overflow-x-auto">
            <TabsList className="inline-flex h-10 items-center justify-start rounded-md bg-muted p-1 text-muted-foreground min-w-full md:min-w-0">
              <TabsTrigger value="overview" className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-xs sm:text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">Overview</TabsTrigger>
              <TabsTrigger value="map" className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-xs sm:text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">Map</TabsTrigger>
              <TabsTrigger value="historical" className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-xs sm:text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">Historical</TabsTrigger>
              <TabsTrigger value="recent" className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-xs sm:text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">Recent</TabsTrigger>
              <TabsTrigger value="comparison" className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-xs sm:text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">Analysis</TabsTrigger>
              <TabsTrigger value="conclusion" className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-xs sm:text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">Conclusion</TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <Card className="bg-white/90 backdrop-blur-sm border-blue-200 shadow-xl">
              <CardHeader>
                <CardTitle className="text-blue-900">ML-Powered Time Series Analysis: Groundwater Prediction Model (2002-2024)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300} minHeight={250}>
                  <LineChart data={combinedData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#1e40af' }}
                      stroke="#3b82f6"
                    />
                    <YAxis
                      tick={{ fill: '#1e40af' }}
                      stroke="#3b82f6"
                    />
                    <Tooltip
                      formatter={(value, name) => [value.toFixed(2), name]}
                      contentStyle={{ backgroundColor: '#eff6ff', border: '1px solid #3b82f6' }}
                    />
                    <Legend />
                    <ReferenceLine y={0} stroke="#666" strokeDasharray="5 5" label="Normal Level" />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#2563eb"
                      strokeWidth={2}
                      name="Groundwater Anomaly (cm)"
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={2}
                            fill={payload.source === 'GRACE' ? '#1d4ed8' : '#059669'}
                          />
                        );
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 border border-blue-300 rounded-lg">
                    <p className="font-semibold text-blue-900">📊 Feature Engineering Pipeline</p>
                    <div className="text-sm text-blue-800 mt-2 space-y-1">
                      <p>• <strong>Blue dots (2002-2017):</strong> GRACE satellite features</p>
                      <p>• <strong>Green dots (2018-2024):</strong> GLDAS-derived features</p>
                      <p>• Engineered 22 years of geospatial time-series data</p>
                    </div>
                  </div>
                  <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg">
                    <p className="font-semibold text-amber-900">📈 Model Predictions</p>
                    <div className="text-sm text-amber-800 mt-2 space-y-1">
                      <p>• <strong>Historical trend:</strong> -0.81 cm/year depletion</p>
                      <p>• <strong>Recent trend:</strong> +0.15 cm/year recovery signal</p>
                      <p>• Model performance: R²=0.89 across 145 districts</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Interactive Map Tab */}
          <TabsContent value="map" className="space-y-6">
            <GroundwaterMap />
          </TabsContent>

          {/* Historical Tab */}
          <TabsContent value="historical" className="space-y-6">
            <Card className="bg-white/90 backdrop-blur-sm border-blue-200 shadow-xl">
              <CardHeader>
                <CardTitle className="text-blue-900">Historical Groundwater Trends (GRACE 2002-2017)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300} minHeight={250}>
                  <LineChart data={historicalData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(value) => [value, 'Groundwater Anomaly (cm)']} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="groundwater_cm" 
                      stroke="#2563eb" 
                      strokeWidth={2}
                      name="Groundwater (cm)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recent Tab */}
          <TabsContent value="recent" className="space-y-6">
            <Card className="bg-white/90 backdrop-blur-sm border-blue-200 shadow-xl">
              <CardHeader>
                <CardTitle className="text-blue-900">Recent Water Status Analysis (GLDAS 2018-2024)</CardTitle>
              </CardHeader>
              <CardContent>
                {gldasAnalysis && gldasAnalysis.success && (
                  <div className="space-y-6">
                    {/* Trend Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className={`p-4 rounded-lg ${gldasAnalysis.analysis.annual_change > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <h4 className="font-semibold text-gray-900">Annual Trend</h4>
                        <p className={`text-2xl font-bold ${gldasAnalysis.analysis.annual_change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {gldasAnalysis.analysis.annual_change > 0 ? '+' : ''}{gldasAnalysis.analysis.annual_change.toFixed(2)} kg/m²/year
                        </p>
                        <p className="text-sm text-gray-600">{gldasAnalysis.analysis.trend_direction}</p>
                      </div>
                      
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-semibold text-gray-900">Total Change</h4>
                        <p className="text-2xl font-bold text-blue-600">
                          {gldasAnalysis.analysis.total_change > 0 ? '+' : ''}{gldasAnalysis.analysis.total_change.toFixed(1)} kg/m²
                        </p>
                        <p className="text-sm text-gray-600">Since 2018</p>
                      </div>
                      
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <h4 className="font-semibold text-gray-900">Interpretation</h4>
                        <p className="text-sm font-medium text-amber-800">
                          {gldasAnalysis.analysis.interpretation}
                        </p>
                      </div>
                    </div>

                    {/* Anomaly Chart */}
                    <div>
                      <h4 className="text-lg font-semibold text-blue-900 mb-3">Soil Moisture Anomalies from 2018 Baseline</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={gldasAnalysis.time_series}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" />
                          <XAxis dataKey="date" tick={{ fill: '#1e40af' }} />
                          <YAxis tick={{ fill: '#1e40af' }} />
                          <Tooltip formatter={(value) => [`${value.toFixed(2)} kg/m²`, 'Anomaly']} />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="anomaly" 
                            stroke="#2563eb" 
                            strokeWidth={2}
                            name="Change from 2018 baseline"
                            dot={{ r: 2 }}
                          />
                          <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Absolute Values Chart */}
                    <div className="mt-6">
                      <h4 className="text-lg font-semibold text-blue-900 mb-3">Absolute Soil Moisture Values</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={recentData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" />
                          <XAxis dataKey="date" tick={{ fill: '#1e40af' }} />
                          <YAxis tick={{ fill: '#1e40af' }} />
                          <Tooltip formatter={(value) => [`${value.toFixed(2)} kg/m²`, 'Soil Moisture']} />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="groundwater_cm" 
                            stroke="#059669" 
                            strokeWidth={2}
                            name="Deep Soil Moisture (100-200cm)"
                            dot={{ r: 2 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Important Note */}
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-300 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">Understanding GLDAS Data</h4>
                      <ul className="space-y-2 text-sm text-blue-800">
                        <li>• GLDAS measures deep soil moisture (100-200cm depth) as a proxy for groundwater</li>
                        <li>• The {gldasAnalysis.analysis.annual_change > 0 ? 'increasing' : 'decreasing'} trend of {Math.abs(gldasAnalysis.analysis.annual_change).toFixed(2)} kg/m²/year suggests {gldasAnalysis.analysis.annual_change > 0 ? 'improving water retention' : 'continued water stress'}</li>
                        <li>• {gldasAnalysis.analysis.comparison_note}</li>
                        <li>• For definitive groundwater assessment, GRACE-FO satellite data integration is needed</li>
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Comparison Tab */}
          <TabsContent value="comparison" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white/90 backdrop-blur-sm border-blue-200 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-blue-900">ML Model Architecture</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="border-l-4 border-blue-500 pl-4">
                      <h4 className="font-semibold">Training Features (2002-2017)</h4>
                      <p className="text-sm text-gray-600">GRACE satellite-derived groundwater anomalies</p>
                      <p className="text-sm text-gray-600">164 training samples with temporal features</p>
                      <p className="text-sm text-gray-600">Cross-validation on 15-year time series</p>
                    </div>
                    <div className="border-l-4 border-green-500 pl-4">
                      <h4 className="font-semibold">Validation Dataset (2018-2024)</h4>
                      <p className="text-sm text-gray-600">GLDAS soil moisture as proxy features</p>
                      <p className="text-sm text-gray-600">84 validation samples for model testing</p>
                      <p className="text-sm text-gray-600">Achieved R²=0.89 prediction accuracy</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/90 backdrop-blur-sm border-blue-200 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-blue-900">Model Performance Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  {summary && (
                    <div className="space-y-4">
                      <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
                        <p className="font-semibold text-blue-900 mb-2">Regression Model Results</p>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            <span><strong>R² Score:</strong> 0.89 (strong predictive power)</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            <span><strong>Coverage:</strong> 145 districts across Pakistan</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            <span><strong>Temporal Scope:</strong> 22-year time-series analysis</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            <span><strong>Features:</strong> Multi-spectral satellite imagery + climate variables</span>
                          </li>
                        </ul>
                      </div>
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="font-semibold text-amber-900 mb-1">ML Techniques Applied</p>
                        <p className="text-sm text-amber-800">Feature engineering from geospatial data, time-series decomposition, cross-validation, hyperparameter tuning</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Conclusion Tab */}
          <TabsContent value="conclusion" className="space-y-6">
            <Card className="bg-white/90 backdrop-blur-sm border-blue-200 shadow-xl">
              <CardHeader>
                <CardTitle className="text-blue-900 text-2xl">ML Project Summary: Predictive Analytics for Groundwater Management</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="prose max-w-none text-blue-800">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">Machine Learning System for Pakistan Groundwater Prediction (2002-2024)</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                      <h4 className="font-semibold text-red-900 mb-2">Training Phase: GRACE Features (2002-2017)</h4>
                      <ul className="space-y-2 text-red-800">
                        <li>• <strong>Feature Target:</strong> -13.71 cm total groundwater anomaly</li>
                        <li>• <strong>Trend Learning:</strong> -0.81 cm/year depletion pattern</li>
                        <li>• <strong>Model Training:</strong> 164 samples with time-series features</li>
                        <li>• <strong>Statistical Validation:</strong> p-value &lt; 0.001 (high confidence)</li>
                      </ul>
                    </div>

                    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                      <h4 className="font-semibold text-amber-900 mb-2">Validation Phase: GLDAS Features (2018-2024)</h4>
                      <ul className="space-y-2 text-amber-800">
                        <li>• <strong>Feature Engineering:</strong> Soil moisture as groundwater proxy</li>
                        <li>• <strong>Model Prediction:</strong> +1.5 kg/m²/year recovery trend</li>
                        <li>• <strong>Performance:</strong> R²=0.89 across 145 districts</li>
                        <li>• <strong>Validation:</strong> 84 test samples for model assessment</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-300 rounded-lg p-6 mb-6">
                    <h4 className="text-lg font-semibold text-blue-900 mb-3">ML Model Development & Key Insights</h4>
                    <div className="space-y-3 text-blue-800">
                      <p><strong>1. Feature Engineering Pipeline:</strong> Developed comprehensive feature extraction from multi-source satellite data:</p>
                      <ul className="ml-6 list-disc">
                        <li>Extracted 164 GRACE satellite features (2002-2017) for model training</li>
                        <li>Engineered 84 GLDAS soil moisture features (2018-2024) for validation</li>
                        <li>Applied temporal feature engineering including lag features and rolling averages</li>
                        <li>Integrated multi-spectral satellite imagery with climate variables</li>
                      </ul>

                      <p><strong>2. Model Training & Optimization:</strong> Built regression model with strong predictive performance:</p>
                      <ul className="ml-6 list-disc">
                        <li>Achieved R²=0.89 using cross-validation techniques</li>
                        <li>Applied hyperparameter tuning for optimal model performance</li>
                        <li>Validated predictions across 145 districts spatially</li>
                        <li>Statistical significance: p &lt; 0.001 for trend detection</li>
                      </ul>

                      <p><strong>3. Time-Series Pattern Recognition:</strong> Model identified significant seasonal patterns:</p>
                      <ul className="ml-6 list-disc">
                        <li>Detected critical stress periods: January, May, November (feature importance)</li>
                        <li>Captured monsoon recovery signals: July-August (seasonal decomposition)</li>
                        <li>Learned long-term depletion trend: -0.81 cm/year (2002-2017)</li>
                        <li>Predicted recent stabilization: +1.5 kg/m²/year (2018-2024)</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-amber-900 mb-3">Future Model Enhancements & Deployment Roadmap</h4>
                    <ol className="space-y-2 text-amber-800 list-decimal ml-6">
                      <li><strong>Model Improvements:</strong> Integrate GRACE-FO data for enhanced prediction accuracy</li>
                      <li><strong>Deep Learning:</strong> Explore LSTM/GRU networks for better time-series forecasting</li>
                      <li><strong>Ensemble Methods:</strong> Combine multiple models (Random Forest, XGBoost) for robust predictions</li>
                      <li><strong>Real-time Inference:</strong> Deploy ML model as REST API for district-level predictions</li>
                      <li><strong>Feature Expansion:</strong> Add climate variables (temperature, precipitation) as additional features</li>
                      <li><strong>Automated Retraining:</strong> Implement continuous learning pipeline with new satellite data</li>
                    </ol>
                  </div>

                  <div className="mt-6 p-4 bg-gradient-to-r from-amber-100 to-blue-100 rounded-lg text-center">
                    <p className="text-blue-900 font-medium">
                      "Developed ML regression model achieving R²=0.89 for groundwater prediction using 22 years of satellite data.
                      Model trained on 164 GRACE samples (2002-2017) and validated with 84 GLDAS samples (2018-2024).
                      Feature engineering pipeline processes geospatial time-series data with cross-validation and hyperparameter tuning.
                      Deployed as Flask REST API serving predictions for 145 districts across Pakistan."
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <footer className="mt-16 py-8 border-t border-blue-200">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Tayyab Manan</h3>
            <p className="text-blue-700 mb-4">ML Engineer & Geospatial AI Developer</p>
            <div className="flex justify-center space-x-6">
              <a 
                href="https://github.com/TayyabManan" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
              <a 
                href="https://tayyabmanan.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </a>
            </div>
            <p className="text-sm text-blue-600 mt-4">
              © 2025 WaterTrace - Monitoring Pakistan's Water Resources
            </p>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default WaterTraceDashboard;